import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { FileUpload } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import moment from 'moment';
import * as XLSX from 'xlsx';
import '../custom.css';
import './CategoryMaster.css';

export default function MasterList() {
    const toast = useRef(null);

    const [masters, setMasters] = useState([]);
    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingMaster, setEditingMaster] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
            clientid: null,
            branchid: null,
            platformid: null
        });

    const [formData, setFormData] = useState({
        id: null,
        platformid: "",
        master_type: "",
        master_details: "",
        master_image: null,
        master_image_url: null,
        status: "Active",
        effective_from: null
    });

    const [errors, setErrors] = useState({});

    const formatDate = (dateString) => {
        return moment(dateString).isValid()
          ? moment(dateString).format('DD-MM-YYYY')
          : '';
    };

    const exportToExcel = () => {
        const exportData = masters.map((row, i) => ({
            '#': i + 1,
            Type: row.master_type || '',
            Details: row.master_details || '',
            Status: row.master_status || '',
            'Effective From': formatDate(row.effective_from),
            'Data Transfer': row.data_transfer || '',
            Client: row.client_name || '',
            Branch: row.branch_name || ''
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Masters');
        XLSX.writeFile(wb, `MastersList_${moment().format('DDMMYYYY')}.xlsx`);
    };

    useEffect(() => {
        fetchClients();
        fetchPlatforms();
        fetchMasters();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
            setClients(res.data.map(c => ({ label: c.client_name, value: c.client_id })));
        } catch {
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load clients" });
        }
    };

    const fetchBranches = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', { params: { clientid: clientId } });
            setBranches(res.data.map(b => ({ label: b.branch_name, value: b.branch_id })));
        } catch {
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load branches" });
        }
    };

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms((res.data || []).map((p) => ({
                label: p.platform_name,
                value: String(p.platform_id),
            })));
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMasters = async (appliedFilters = {}) => {
        setLoading(true); //start loading
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/masters_creation', { params: appliedFilters });

            if (res.data && res.data.status === "success" && Array.isArray(res.data.masters)) {
                setMasters(res.data.masters);
            } else {
                setMasters([]);
            }
        } catch (err) {
            console.error("Error fetching masters:", err);
            setMasters([]);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to load masters",
            });
        }  finally {
            setLoading(false); //stop loading
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };

        if (key === "clientid") {
            fetchBranches(value);
            newFilters.branchid = null;
        }

        setFilters(newFilters);
        // if no filters applied, fetch all
        const activeFilters = Object.fromEntries(
            Object.entries(newFilters).filter(([_, v]) => v)
        );
        fetchMasters(activeFilters);
    };

    const onInputChange = (e, name) => {
        setFormData({ ...formData, [name]: e.target.value });
    };

    // Handle file change
    const onFileSelect = (e) => {
        setFormData({ ...formData, master_image: e.files[0] });
    };

    // Validate form
    const validateForm = () => {
        let newErrors = {};

        if (!formData.platformid || !formData.platformid.trim()) {
            newErrors.platformid = "Platform is required";
        }

        if (!formData.master_type || !formData.master_type.trim()) {
            newErrors.master_type = "Master type is required";
        }

        if (!formData.master_details || !formData.master_details.trim()) {
            newErrors.master_details = "Master detail is required";
        }

        if (!formData.effective_from) {
            newErrors.effective_from = "Effective from is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // open dialog for edit
    const editMaster = (rowData) => {
        setEditingMaster(true);
        setFormData({
            id: rowData.masterId ,
            platformid: rowData.platform_id ? String(rowData.platform_id) : "",
            master_type: rowData.master_type,
            master_details: rowData.master_details,
            master_image: null,
            master_image_url: rowData.master_image_path,
            status: rowData.master_status,
            client: rowData.client_name,
            branch: rowData.branch_name,
            effective_from: rowData.effective_from
                        ? new Date(rowData.effective_from)
                        : null
        });
        setVisible(true);
    };

    // Save or Update Master
    const saveMaster = async () => {
        if (!validateForm()) {
            toast.current.show({ severity: "error", summary: "Validation Error", detail: "Please fix errors before saving" });
            return;
        }

        try {
            const url = editingMaster ? 'https://treatway.co.in/Counter-Backend/api/server_masters/save/masters_creation' : 'https://treatway.co.in/Counter-Backend/api/server_masters/save/masters_creation';

            const data = new FormData();
            data.append("id", formData.id || "");
            data.append("platformid", formData.platformid);
            data.append("master_type", formData.master_type);
            data.append("master_details", formData.master_details);
            data.append("status", formData.status);
            data.append("effective_from", formData.effective_from ? formData.effective_from.toISOString().split("T")[0] : "");
            if (formData.master_image_url) {
                data.append("master_image_url", formData.master_image_url);
            }
            if (formData.master_image) {
                data.append("master_image", formData.master_image);
            }

            await axios.post(url, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: editingMaster ? "Master Updated" : "Master Added"
            });

            setVisible(false);
            setEditingMaster(null);
            setFormData({ id: null, platformid: "", master_type: "", master_details: "", master_image: null, status: "Active", effective_from: null });
            fetchMasters(); // refresh table
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
        }
    };

    // Dialog footer
    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveMaster} />
        </div>
    );

    const statusOptions = [
        { label: "Active", value: "Active" },
        { label: "InActive", value: "InActive" }
    ];

    return (
        <>
            <Toast ref={toast} />
            <div className="masters-page-header">
                <h2>Masters List</h2>
                <p>View and manage master creation records</p>
            </div>
            <div className="list-filter-bar">
                <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                    <label>Client</label>
                    <Dropdown value={filters.clientid} options={clients} onChange={(e) => handleFilterChange('clientid', e.value)} placeholder="Select Client" className="w-full" filter showClear />
                </div>
                <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                    <label>Branch</label>
                    <Dropdown value={filters.branchid} options={branches} onChange={(e) => handleFilterChange('branchid', e.value)} placeholder="Select Branch" className="w-full" filter showClear />
                </div>
                <div className="filter-field" style={{ flex: '1 1 155px', minWidth: '135px' }}>
                    <label>Platform</label>
                    <Dropdown value={filters.platformid} options={platforms} onChange={(e) => handleFilterChange('platformid', e.value)} placeholder="Select Platform" className="w-full" filter showClear />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-secondary"
                        tooltip="Reset Filters"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                            setFilters({ clientid: null, branchid: null, platformid: null });
                            setBranches([]);
                            fetchMasters();
                        }}
                    />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button icon="pi pi-file-excel" className="masters-excel-btn" tooltip="Export to Excel" tooltipOptions={{ position: 'top' }} onClick={exportToExcel} />
                </div>
                <div className="search-field filter-field">
                    <label>Search</label>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search"></i>
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search masters..." className="p-inputtext-sm w-full" />
                    </span>
                </div>
            </div>

            <div className="card">
                <DataTable
                    value={masters}
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '60rem' }}
                    className="p-datatable-sm relative"
                    loading={loading}
                    loadingIcon={
                        //custom overlay loader
                        <div className="flex align-items-center justify-content-center w-full h-full bg-black-alpha-30 absolute top-0 left-0">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".7s" />
                        </div>
                    }
                    emptyMessage="No masters found."
                    globalFilter={globalFilter}
                >
                    <Column field="master_type" header="Type" />
                    <Column field="master_details" header="Details" className="text-center" />
                    <Column
                        field="master_status"
                        header="Status"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.master_status === 'Active' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.master_status}</span>}
                    />
                    <Column field="effective_from" header="Effective From" body={(rowData) => formatDate(rowData.effective_from)} />
                    <Column
                        field="data_transfer"
                        header="Data Transfer"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.data_transfer === 'updated' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.data_transfer}</span>}
                    />
                    <Column field="client_name" header="Client" />
                    <Column field="branch_name" header="Branch" />
                    <Column header="Actions" body={(rowData) => <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => editMaster(rowData)} />} />
                </DataTable>

                <Dialog header={editingMaster ? 'Edit Master' : 'Add Master'} visible={visible} style={{ width: '450px' }} footer={dialogFooter} onHide={() => setVisible(false)}>
                    <div className="field">
                        <label htmlFor="client">Client</label>
                        <InputText id="client" value={formData.client} className="w-full" readOnly />
                    </div>
                    <div className="field">
                        <label htmlFor="client">Branch</label>
                        <InputText id="client" value={formData.branch} className="w-full" readOnly />
                    </div>
                    <div className="field">
                        <label htmlFor="platformid">Platform</label>
                        <Dropdown id="platformid" value={formData.platformid} options={platforms} onChange={(e) => setFormData({ ...formData, platformid: e.value })} placeholder="Select a Platform" className="w-full" />
                        {errors.platformid && <small className="p-error">{errors.platformid}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_type">Master Type</label>
                        <InputText id="master_type" value={formData.master_type} onChange={(e) => onInputChange(e, 'master_type')} className="w-full" />
                        {errors.master_type && <small className="p-error">{errors.master_type}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_details">Master Details</label>
                        <InputText id="master_details" value={formData.master_details} onChange={(e) => onInputChange(e, 'master_details')} className="w-full" />
                        {errors.master_details && <small className="p-error">{errors.master_details}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_image">Master Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setFormData({ ...formData, master_image: file, preview: URL.createObjectURL(file) });
                                }
                            }}
                            className="w-full"
                        />
                        {formData.preview && <img src={formData.preview} alt="Preview" className="mt-2 border rounded" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />}
                        {/* Show old image if editing and no new file chosen */}
                        {!formData.preview && editingMaster && formData.master_image_url && (
                            <img src={`https://treatway.co.in/Counter-Backend/uploads/masters/${formData.master_image_url}`} alt="No Image" className="mt-2 border rounded" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                        )}
                    </div>

                    <div className="field">
                        <label htmlFor="status">Status</label>
                        <Dropdown id="status" value={formData.status} options={statusOptions} onChange={(e) => setFormData({ ...formData, status: e.value })} className="w-full" />
                    </div>

                    <div className="field">
                        <label htmlFor="effective_from">Effective From</label>
                        <Calendar id="effective_from" value={formData.effective_from} onChange={(e) => setFormData({ ...formData, effective_from: e.value })} dateFormat="dd-mm-yy" showIcon className="w-full" />
                        {errors.effective_from && <small className="p-error">{errors.effective_from}</small>}
                    </div>
                </Dialog>
            </div>
        </>
    );
}
